import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledInput from 'src/components/form/controlled-input';
import ControlledSelect from 'src/components/form/controlled-select';
import { ControlledSwitch } from 'src/components/form/controlled-switch/ControlledSwitch';
import { CustomisableForm } from 'src/components/form/form/CustomisableForm';
import { FormField } from 'src/components/form/form/FormField';
import { ModalWrapper } from 'src/components/form/form/ModalWrapper';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';

import { emptyFilterQuery } from '@/utils/collectionUtils';

import type { WidgetDataSource } from '../../gigawidget/types';
import { WidgetPropertyFilter } from '../../universal-widget/form/WidgetPropertyFilter';
import type { RiskBubbleChartSettings } from './riskBubbleChartSettingsSchema';
import {
  bubbleSizeEncodings,
  resolveBubbleSizeEncoding,
  riskBubbleChartSettingsSchema,
} from './riskBubbleChartSettingsSchema';
import { RiskBubbleChartSettingsTestIds } from './RiskBubbleChartSettingsTestIds';

type FieldsProps<TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
};

const RiskBubbleChartSettingsFormFields = <
  TDataSource extends WidgetDataSource,
>({
  dataSource,
}: FieldsProps<TDataSource>) => {
  const { control, watch } = useFormContext<RiskBubbleChartSettings>();
  const { t } = useTranslation('common', {
    keyPrefix: 'dashboard.widgetSettings.fields',
  });
  const { t: sizeT } = useTranslation('common', {
    keyPrefix: 'dashboard.widgetSettings.fields.bubbleSizeOptions',
  });

  const customTitle = watch('customTitle');

  return (
    <div>
      <ControlledSelect
        testId={RiskBubbleChartSettingsTestIds.BubbleSize}
        name={'bubbleSize'}
        label={t('bubbleSize')}
        control={control}
        options={bubbleSizeEncodings.map((encoding) => ({
          value: encoding,
          label: sizeT(encoding),
        }))}
      />
      <Controller
        control={control}
        render={({ field: { value, onChange } }) => (
          <>
            <FormField label={t('filtering')}>
              <WidgetPropertyFilter
                value={value}
                onChange={onChange}
                dataSource={dataSource}
              />
            </FormField>
            <div className={'mb-4'}>
              <ControlledSwitch
                name={'customTitle'}
                label={t('customTitle')}
                control={control}
              />
            </div>
            {customTitle && (
              <ControlledInput
                control={control}
                name={'title'}
                label={t('customTitle')}
              />
            )}
          </>
        )}
        name={'filtering'}
      />
    </div>
  );
};

type Props<TDataSource extends WidgetDataSource> = {
  onDismiss: () => void;
  onSave: (data: RiskBubbleChartSettings) => Promise<void>;
  dataSource: TDataSource;
};

/**
 * The bubble chart's settings: the shared filter and custom title, plus what
 * bubble size encodes (RSP-7292). Fixed size until the user chooses a measure.
 */
export const RiskBubbleChartSettingsModal = <
  TDataSource extends WidgetDataSource,
>({
  onDismiss,
  onSave,
  dataSource,
}: Props<TDataSource>) => {
  const { t } = useTranslation('common');
  const [settings] = useDashboardWidgetSettings<RiskBubbleChartSettings>();

  const values: RiskBubbleChartSettings = {
    filtering: emptyFilterQuery,
    customTitle: false,
    ...(settings ?? {}),
    // Stored settings may predate the setting, or carry a value this build
    // no longer offers; the select must show what the chart actually draws.
    bubbleSize: resolveBubbleSizeEncoding(settings),
  };

  return (
    <CustomisableForm
      onSave={onSave}
      defaultValues={values}
      formId={'risk-bubble-chart-form'}
      schema={riskBubbleChartSettingsSchema}
      i18n={t('widget')}
      renderTemplate={(renderProps) => (
        <ModalWrapper {...renderProps} visible={true} />
      )}
      onDismiss={onDismiss}
    >
      <RiskBubbleChartSettingsFormFields dataSource={dataSource} />
    </CustomisableForm>
  );
};
